import { useMemo, useState, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableRow, TablePagination,
  TableContainer, TableSortLabel, Box, Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDateSort } from '../../hooks/useDateSort';
import TableActionsMenu from '../../components/common/TableActionsMenu';
import type { DailyRevenueItem } from '../../types/pointhome.types';
import type { Dayjs } from 'dayjs';

type Props = { 
  data: DailyRevenueItem[];
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
};

export default function PointhomeDailyReportTable({ data, selectedApp, startDate, endDate }: Props) {
  const rowsPerPage = 31;
  const [page, setPage] = useState(0);
  const { sorted: sortedData, sortDirection, toggleSort } = useDateSort(data);
  
  // 테이블 ref - 엑셀 다운로드용
  const tableRef = useRef<HTMLTableElement>(null);

  // 파일명 생성
  const filename = useMemo(() => {
    const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
    return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
  }, [startDate, endDate, selectedApp]);

  const paged = useMemo(
    () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedData, page]
  );

  // 합계 계산 (DAU는 평균, 나머지는 합계)
  const totals = useMemo(() => {
    const totalDau = sortedData.reduce((sum, row) => sum + (row.DAU ?? 0), 0);
    const avgDau = sortedData.length > 0 ? Math.round(totalDau / sortedData.length) : 0;
    const totalRevenue = sortedData.reduce((sum, row) => sum + (row.Revenue ?? 0), 0);
    const totalSSP = sortedData.reduce((sum, row) => sum + (row.SSP ?? 0), 0);
    const totalBuzzvil = sortedData.reduce((sum, row) => sum + (row.Buzzvil ?? 0), 0);
    const totalOfferwall = sortedData.reduce((sum, row) => sum + (row.Offerwall ?? 0), 0);
    const totalCPC = sortedData.reduce((sum, row) => sum + (row.CPC ?? 0), 0);
    const totalUsedCash = sortedData.reduce((sum, row) => sum + (row.UsedCash ?? 0), 0);

    return {
      avgDau,
      totalRevenue,
      totalSSP,
      totalBuzzvil,
      totalOfferwall,
      totalCPC,
      totalUsedCash
    };
  }, [sortedData]);

  // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시)
  // 합계 행은 exportTableToExcelWithAllRows에서 자동으로 감지되어 추가됨
  const allRowsDataForExcel = useMemo(() => {
    return sortedData.map((row) => {
      return [
        row.date,
        (row.DAU ?? 0).toLocaleString(),
        row.Revenue.toLocaleString(),
        (row.SSP ?? 0).toLocaleString(),
        (row.Buzzvil ?? 0).toLocaleString(),
        (row.Offerwall ?? 0).toLocaleString(),
        (row.CPC ?? 0).toLocaleString(),
        (row.UsedCash ?? 0).toLocaleString()
      ];
    });
  }, [sortedData]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu 
          tableRef={tableRef} 
          allRowsData={allRowsDataForExcel}
          filename={filename}
          sheetName="일별레포트"
        />
      </Box>
      <TableContainer
        sx={{
          overflowX: 'auto'
        }}
      >
      <Table ref={tableRef}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active
                direction={sortDirection}
                onClick={toggleSort}
              >
                구분
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold'}}>DAU</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold'}}>총 매출</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold'}}>애드팝콘 SSP</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold'}}>버즈빌</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold'}}>오퍼월</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold'}}>CPC 광고</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold'}}>캐시사용</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* 합계 행 */}
          <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>합계</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {totals.avgDau.toLocaleString()}
                <Tooltip title="조회한 기간의 평균입니다" arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                </Tooltip>
              </Box>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {totals.totalRevenue.toLocaleString()}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {totals.totalSSP.toLocaleString()}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {totals.totalBuzzvil.toLocaleString()}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {totals.totalOfferwall.toLocaleString()}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {totals.totalCPC.toLocaleString()}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {totals.totalUsedCash.toLocaleString()}
            </TableCell>
          </TableRow>
          {paged.map((row) => {
            return (
              <TableRow key={row.date}>
                <TableCell>{row.date}</TableCell>
                <TableCell align="right">{(row.DAU ?? 0).toLocaleString()}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'gray' }}>
                  {row.Revenue.toLocaleString()}
                </TableCell>
                <TableCell align="right">{(row.SSP ?? 0).toLocaleString()}</TableCell>
                <TableCell align="right">{(row.Buzzvil ?? 0).toLocaleString()}</TableCell>
                <TableCell align="right">{(row.Offerwall ?? 0).toLocaleString()}</TableCell>
                <TableCell align="right">{(row.CPC ?? 0).toLocaleString()}</TableCell>
                <TableCell align="right">{(row.UsedCash ?? 0).toLocaleString()}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={sortedData.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}   // 고정 14일
      />
    </TableContainer>
    </Box>
  );
}
